import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { rupiah, terbilang, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function Receipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/payments/${id}`).then((res) => setP(res.data)).catch(() => setError("Kwitansi tidak ditemukan."));
    api.get("/tenant/profile").then((res) => setTenant(res.data)).catch(() => {});
  }, [id]);

  if (error) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground" data-testid="receipt-error">{error}</div>;
  }
  if (!p) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground" data-testid="receipt-loading">Memuat kwitansi...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 py-10">
      <div className="mx-auto max-w-2xl px-4">
        <div className="mb-4 flex items-center justify-between print:hidden">
          <Button variant="outline" onClick={() => navigate(-1)} className="gap-2" data-testid="receipt-back-button">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </Button>
          <Button onClick={() => window.print()} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="receipt-print-button">
            <Printer className="h-4 w-4" /> Cetak Kwitansi
          </Button>
        </div>

        <div id="receipt-print" className="rounded-xl border border-border bg-white p-8 shadow-sm" data-testid="receipt-document">
          <div className="flex items-start justify-between border-b-2 border-primary pb-5">
            <div className="flex items-center gap-4">
              {tenant?.logo_url ? (
                <img src={tenant.logo_url} alt={tenant.name} className="h-14 w-auto object-contain" data-testid="receipt-logo" />
              ) : (
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary font-heading text-2xl font-bold text-primary-foreground" data-testid="receipt-logo">
                  {(tenant?.name || "S").charAt(0)}
                </div>
              )}
              <div>
                <p className="font-heading text-sm font-bold" data-testid="receipt-school-name">{tenant?.name || "Sekolah Musik"}</p>
                <p className="text-xs text-muted-foreground" data-testid="receipt-school-address">
                  {[tenant?.address, tenant?.phone ? `Telp ${tenant.phone}` : null].filter(Boolean).join(" • ") || "-"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-heading text-xl font-bold tracking-widest text-primary">KWITANSI</p>
              <p className="font-mono text-xs text-muted-foreground" data-testid="receipt-number">{p.receipt_no}</p>
            </div>
          </div>

          <table className="mt-6 w-full text-sm">
            <tbody>
              <tr>
                <td className="w-44 py-2.5 align-top text-muted-foreground">Telah diterima dari</td>
                <td className="py-2.5 font-semibold" data-testid="receipt-parent">{p.parent_name !== "-" ? `${p.parent_name} (Ortu/Wali)` : p.student_name}</td>
              </tr>
              <tr>
                <td className="py-2.5 align-top text-muted-foreground">Untuk pembayaran</td>
                <td className="py-2.5 font-semibold" data-testid="receipt-description">
                  {p.payment_type === "SPP"
                    ? `SPP ${p.month_name} ${p.year} — ${p.student_name} (${p.class_name})`
                    : `${p.payment_type}${p.description ? ` — ${p.description}` : ""} — ${p.student_name} (${p.class_name})`}
                </td>
              </tr>
              <tr>
                <td className="py-2.5 align-top text-muted-foreground">Metode pembayaran</td>
                <td className="py-2.5 font-semibold">{p.method}</td>
              </tr>
              {p.notes && (
                <tr>
                  <td className="py-2.5 align-top text-muted-foreground">Catatan</td>
                  <td className="py-2.5">{p.notes}</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Jumlah</p>
            <p className="font-heading text-3xl font-bold text-primary" data-testid="receipt-amount">{rupiah(p.amount)}</p>
            <p className="mt-1 text-sm italic text-muted-foreground" data-testid="receipt-terbilang">Terbilang: {terbilang(p.amount)} Rupiah</p>
          </div>

          <div className="mt-10 flex items-end justify-between">
            <div className="text-xs text-muted-foreground">
              <p>Tanggal: {formatDate(p.paid_at)}</p>
              <p className="mt-1">Kwitansi ini sah tanpa materai sebagai bukti pembayaran yang valid.</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Hormat kami,</p>
              <div className="my-10" />
              <p className="border-t border-foreground/30 px-6 pt-1 text-sm font-semibold" data-testid="receipt-admin-name">{p.created_by || "Admin"}</p>
              <p className="text-xs text-muted-foreground">Administrasi</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
